import React, { useEffect, useState, useRef } from 'react';
interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;
  variant?: 'primary' | 'secondary';
  className?: string;
  href?: string;
}
export function Button({
  children,
  variant = 'primary',
  className = '',
  href,
  ...props
}: ButtonProps) {
  const ref = useRef<HTMLButtonElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !hasAnimated) {
        setIsVisible(true);
        setHasAnimated(true);
        setTimeout(() => setIsVisible(false), 1200);
      }
    }, {
      threshold: 0.5
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasAnimated]);
  const baseStyles = 'inline-flex items-center justify-center font-ui uppercase tracking-wide rounded-sm cursor-pointer transition-all duration-500 ease-out';
  const variantClasses = {
    primary: 'font-semibold text-base px-10 py-4 hover:-translate-y-[1px]',
    secondary: 'font-medium text-sm px-8 py-3 hover:shadow-gold-subtle'
  };
  const variantInlineStyles: Record<string, React.CSSProperties> = {
    primary: {
      background: 'linear-gradient(135deg, #C9A84C 0%, #F0D98A 50%, #C9A84C 100%)',
      color: '#1A1714',
      boxShadow: isVisible ? '0 0 32px rgba(201,168,76,0.45), 0 0 64px rgba(201,168,76,0.2)' : '0 2px 12px rgba(201,168,76,0.15)',
      transform: isVisible ? 'scale(1.05)' : undefined
    },
    secondary: {
      background: 'transparent',
      color: '#C9A84C',
      border: '1px solid #8B6914',
      boxShadow: isVisible ? '0 0 32px rgba(201,168,76,0.45), 0 0 64px rgba(201,168,76,0.2)' : undefined,
      transform: isVisible ? 'scale(1.05)' : undefined
    }
  };
  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (href) {
      e.preventDefault();
      if (href.startsWith('http')) {
        window.open(href, '_blank', 'noopener,noreferrer');
        return;
      }
      const target = document.querySelector(href);
      if (target) {
        target.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    }
    props.onClick?.(e);
  };
  return <button ref={ref} className={`${baseStyles} ${variantClasses[variant]} ${className}`} style={variantInlineStyles[variant]} {...props} onClick={handleClick} data-id="element-650">
      {children}
    </button>;
}